import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import { getWarmupPlan, WarmupPlan } from "../../../../helpers/warmup_helper";

/**
 * Hub Entregabilidade — warm-up assistido (LAC-125).
 * Por quê página própria: volume diário ≠ Pré-voo de conteúdo.
 */
const DeliverabilityHubPage = () => {
  const { t, i18n } = useTranslation();
  document.title = t("settings.deliverability.documentTitle");
  const helpHref = helpUrlForRoute(
    "/settings/deliverability",
    i18n.language || "pt-BR"
  );

  const [plan, setPlan] = useState<WarmupPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWarmupPlan();
      setPlan(res.data);
    } catch (e: any) {
      setError(e?.message || t("settings.deliverability.loadError"));
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const stageLabel = (stage: string) =>
    t(`settings.deliverability.stage.${stage}`, { defaultValue: stage });

  const progressPct =
    plan?.suggestion.progress && plan.suggestion.progress.needed > 0
      ? Math.min(
          100,
          Math.round(
            (plan.suggestion.progress.sent_in_window /
              plan.suggestion.progress.needed) *
              100
          )
        )
      : null;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.deliverability.title")}
          pageTitle={t("menu.account")}
        />
        <DismissiblePageTip tipId="deliverability-hub">
          {t("settings.deliverability.pageTip")}
        </DismissiblePageTip>
        <div className="mb-3">
          <PageHelpLink href={helpHref} label={t("common.help")} />
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <Alert color="danger">{error}</Alert>
        ) : plan ? (
          <>
            {plan.circuit_open ? (
              <Alert color="warning" className="mb-3">
                <strong className="d-block mb-1">
                  {t("settings.deliverability.circuitTitle")}
                </strong>
                {t("settings.deliverability.circuitBody")}
              </Alert>
            ) : null}
            <Row>
              <Col lg={7}>
                <Card className="mb-3">
                  <CardBody>
                    <h5 className="mb-3">
                      {t("settings.deliverability.todayTitle")}
                    </h5>
                    <p className="text-muted mb-2">
                      {t("settings.deliverability.stageLabel")}:{" "}
                      <strong>{stageLabel(plan.stage)}</strong>
                    </p>
                    {plan.circuit_open ? (
                      <p className="mb-0 text-muted">
                        {t("settings.deliverability.circuitEmpty")}
                      </p>
                    ) : plan.daily_cap === null ? (
                      <Alert color="success" className="mb-0">
                        {t("settings.deliverability.trustedBody")}
                      </Alert>
                    ) : (
                      <>
                        <p className="mb-1">
                          {t("settings.deliverability.sentToday", {
                            sent: plan.sent_today.toLocaleString(i18n.language),
                            cap: (plan.daily_cap ?? 0).toLocaleString(
                              i18n.language
                            ),
                          })}
                        </p>
                        <p className="mb-3 text-muted">
                          {t("settings.deliverability.remaining", {
                            remaining: (
                              plan.remaining_today ?? 0
                            ).toLocaleString(i18n.language),
                          })}
                        </p>
                        <Progress
                          value={
                            plan.daily_cap > 0
                              ? Math.min(
                                  100,
                                  Math.round(
                                    (plan.sent_today / plan.daily_cap) * 100
                                  )
                                )
                              : 0
                          }
                          className="mb-0"
                          style={{ height: 10 }}
                        />
                      </>
                    )}
                  </CardBody>
                </Card>

                {!plan.circuit_open && plan.suggestion.progress && (
                  <Card className="mb-3">
                    <CardBody>
                      <h6 className="mb-2">
                        {t("settings.deliverability.nextStageTitle", {
                          stage: stageLabel(
                            plan.suggestion.next_stage || "trusted"
                          ),
                        })}
                      </h6>
                      <p className="small text-muted mb-2">
                        {t("settings.deliverability.nextStageBody", {
                          sent: plan.suggestion.progress.sent_in_window.toLocaleString(
                            i18n.language
                          ),
                          needed:
                            plan.suggestion.progress.needed.toLocaleString(
                              i18n.language
                            ),
                          days: plan.suggestion.progress.window_days,
                        })}
                      </p>
                      {progressPct !== null && (
                        <Progress
                          value={progressPct}
                          color="info"
                          style={{ height: 8 }}
                        />
                      )}
                    </CardBody>
                  </Card>
                )}
              </Col>
              <Col lg={5}>
                <Card className="mb-3">
                  <CardBody>
                    <h6 className="mb-3">
                      {t("settings.deliverability.tipsTitle")}
                    </h6>
                    <ul className="mb-3 ps-3">
                      {plan.suggestion.tip_keys.map((key) => (
                        <li key={key} className="mb-2">
                          {t(`settings.deliverability.tips.${key}`, {
                            defaultValue: key,
                          })}
                        </li>
                      ))}
                    </ul>
                    <div className="d-flex flex-wrap gap-2">
                      <Link to="/campaigns" className="btn btn-primary btn-sm">
                        {t("settings.deliverability.ctaCampaigns")}
                      </Link>
                      <Link
                        to="/settings/sending"
                        className="btn btn-soft-secondary btn-sm"
                      >
                        {t("settings.deliverability.ctaSending")}
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
      </Container>
    </div>
  );
};

export default DeliverabilityHubPage;
